/**
 * svgSolides4e — dessins de solides pour les automatismes 4e (1115, 1116) :
 * prisme droit debout à base polygonale régulière, pyramide à base carrée,
 * cône, boule, et une planche de plusieurs solides côte à côte. Complète les
 * moteurs du 5e (paveNomme, cylindreDebout, prismeTri de svgSolides).
 * Perspective cavalière : k = 0,5, angle 30°, arêtes cachées en pointillés.
 * Revue 2026-09-23 (vague 4), AJOUT pour 185 : solide185 (pyramide, tétraèdre,
 * prismes, cône, cylindre, boule aux proportions réglables, sans sel) et
 * coneVariante (le cône juste et quatre faux) ; les dessins ci-dessous sont
 * inchangés.
 */

// ─── Dessins (perspective cavalière) ───────────────────────────────────────
const R1 = (v) => Math.round(v * 10) / 10;
const K = 0.5; const ALPHA = (30 * Math.PI) / 180;
const proj = (v) => ({ x: v.x + K * v.z * Math.cos(ALPHA), y: -(v.y + K * v.z * Math.sin(ALPHA)) });
const ajuster = (pts, vb, marge = 16) => {
    const xs = pts.map((p) => p.x); const ys = pts.map((p) => p.y);
    const x0 = Math.min(...xs); const x1 = Math.max(...xs); const y0 = Math.min(...ys); const y1 = Math.max(...ys);
    const s = Math.min((vb.w - 2 * marge) / (x1 - x0), (vb.h - 2 * marge) / (y1 - y0));
    const ox = (vb.w - s * (x1 - x0)) / 2 - s * x0; const oy = (vb.h - s * (y1 - y0)) / 2 - s * y0;
    return { map: (p) => ({ x: R1(ox + s * p.x), y: R1(oy + s * p.y) }), s };
};
const ligne = (a, b, dash = false) => `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" stroke="#1f2937" stroke-width="1.7" stroke-linecap="round"${dash ? ' stroke-dasharray="5 4"' : ''}/>`;
const envelopper = (vb, inner, salt) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}" data-v="${salt}">${inner}</svg>`;

/** Prisme droit debout à base polygonale régulière (n = 3, 5 ou 6). */
export const prismePoly = (n, salt) => {
    const vb = { w: 200, h: 200 }; const r = 1.6; const h = 3.2;
    const V = []; for (let i = 0; i < n; i++) { const t = -Math.PI / 2 + (2 * Math.PI * i) / n; V.push({ x: r * Math.cos(t), z: r * Math.sin(t) }); }
    const bas = V.map((v) => proj({ x: v.x, y: 0, z: v.z })); const haut = V.map((v) => proj({ x: v.x, y: h, z: v.z }));
    const { map } = ajuster([...bas, ...haut], vb);
    const B = bas.map(map); const Hh = haut.map(map);
    // arête cachée : sommet à l'arrière ET hors de la silhouette (les sommets extrêmes en x restent visibles)
    // Visibilité (perspective cavalière, observateur du côté z < 0, décalage des fuyantes vers le haut à droite) :
    // une face latérale est visible si sa normale sortante n = (cos θm ; sin θm) vérifie K·cos(α)·cos θm − sin θm > 0 ;
    // une arête est visible si elle borde au moins une face visible (la face du dessus est toujours visible, celle du dessous jamais).
    const faceVisible = []; for (let i = 0; i < n; i++) { const tm = -Math.PI / 2 + (2 * Math.PI * (i + 0.5)) / n; faceVisible.push(K * Math.cos(ALPHA) * Math.cos(tm) - Math.sin(tm) > 0); }
    let s = '';
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n; const prec = (i + n - 1) % n;
        s += ligne(B[i], B[j], !faceVisible[i]);                               // arête de la base : visible si sa face latérale l'est
        s += ligne(Hh[i], Hh[j]);                                              // arêtes du dessus : toujours visibles
        s += ligne(B[i], Hh[i], !faceVisible[i] && !faceVisible[prec]);        // arête verticale : cachée si ses deux faces le sont
    }
    return envelopper(vb, s, salt);
};
/** Pyramide à base carrée (sommet arrière-gauche caché). */
export const pyramide = (salt) => {
    const vb = { w: 200, h: 200 }; const a = 1.5; const h = 3;
    const V = { A: { x: -a, y: 0, z: -a }, B: { x: a, y: 0, z: -a }, C: { x: a, y: 0, z: a }, D: { x: -a, y: 0, z: a }, S: { x: 0, y: h, z: 0 } };
    const P = {}; Object.keys(V).forEach((k) => { P[k] = proj(V[k]); });
    const { map } = ajuster(Object.values(P), vb); Object.keys(P).forEach((k) => { P[k] = map(P[k]); });
    const s = ligne(P.A, P.B) + ligne(P.B, P.C) + ligne(P.C, P.D, true) + ligne(P.D, P.A, true) + ligne(P.S, P.A) + ligne(P.S, P.B) + ligne(P.S, P.C) + ligne(P.S, P.D, true);
    return envelopper(vb, s, salt);
};
/** Cône de révolution debout. */
export const cone = (salt) => {
    const vb = { w: 200, h: 200 }; const cx = 100; const cy = 158; const rx = 62; const ry = 18; const S = { x: cx, y: 30 };
    const s = `<path d="M ${cx - rx},${cy} A ${rx},${ry} 0 0 1 ${cx + rx},${cy}" fill="none" stroke="#1f2937" stroke-width="1.7" stroke-dasharray="5 4"/>`
        + `<path d="M ${cx - rx},${cy} A ${rx},${ry} 0 0 0 ${cx + rx},${cy}" fill="none" stroke="#1f2937" stroke-width="1.7"/>`
        + ligne(S, { x: cx - rx, y: cy }) + ligne(S, { x: cx + rx, y: cy });
    return envelopper(vb, s, salt);
};
/** Boule : un cercle et son équateur. */
export const boule = (salt) => {
    const vb = { w: 200, h: 200 }; const cx = 100; const cy = 100; const r = 70; const ry = 22;
    const s = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#1f2937" stroke-width="1.7"/>`
        + `<path d="M ${cx - r},${cy} A ${r},${ry} 0 0 1 ${cx + r},${cy}" fill="none" stroke="#1f2937" stroke-width="1.4" stroke-dasharray="5 4"/>`
        + `<path d="M ${cx - r},${cy} A ${r},${ry} 0 0 0 ${cx + r},${cy}" fill="none" stroke="#1f2937" stroke-width="1.4"/>`;
    return envelopper(vb, s, salt);
};


/**
 * Planche de plusieurs solides côte à côte, étiquetés (A, B, C, D). Chaque
 * solide est un svg complet (avec son viewBox) : son contenu est extrait et
 * placé dans un groupe translaté / mis à l'échelle (pas de svg imbriqué).
 * `solides` : [{ svg, label, type }] ; data-solides = « A:type|B:type… ».
 */
export const planche = (solides) => {
    const n = solides.length; const w = 150; const h = 170; const W = n * w;
    let g = `<rect x="0" y="0" width="${W}" height="${h}" fill="#ffffff"/>`;
    solides.forEach((sol, i) => {
        // PAS de <svg> imbriqué (le CSS de l'application applique width:100 % à tout svg et fait déborder les cases) :
        // on extrait le contenu et on le place dans un <g> translaté et mis à l'échelle depuis son viewBox
        const vbm = sol.svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/); const vw = vbm ? Number(vbm[1]) : 200; const vh = vbm ? Number(vbm[2]) : 200;
        const contenu = sol.svg.replace(/^<svg [^>]*>/, '').replace(/<\/svg>\s*$/, '');
        const cw = w - 10; const ch = h - 30; const sc = Math.min(cw / vw, ch / vh);
        const tx = i * w + 5 + (cw - sc * vw) / 2; const ty = 22 + (ch - sc * vh) / 2;
        g += `<g transform="translate(${tx.toFixed(1)},${ty.toFixed(1)}) scale(${sc.toFixed(4)})">${contenu}</g>`;
        g += `<text x="${i * w + w / 2}" y="16" font-family="system-ui,sans-serif" font-size="15" font-weight="800" fill="#1e3a8a" text-anchor="middle">${sol.label}</text>`;
    });
    const data = solides.map((sol) => `${sol.label}:${sol.type}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${h}" width="${W}" height="${h}" data-solides="${data}">${g}</svg>`;
};

// ─── Jeu de dessins de 185 (revue 2026-09-23, vague 4) ─────────────────────
// Les conventions des dessins ci-dessus (trait #1f2937 de 1,7, pointillés 5 4,
// cadre 200 × 200, aucune lettre), avec des proportions réglables : la variété
// d'un QCM d'images est ainsi RÉELLE. L'attribut data-v d'un sel la masquait au
// harnais (audit de 185 : 2 questions réelles sur 10 000 tirages) ; aucun de ces
// dessins n'en porte. data-solide dit ce qui est dessiné, pour un futur solveur.

/** Direction de visée de la perspective (un point et ses images le long de VUE se projettent au même endroit). */
const VUE = { x: -K * Math.cos(ALPHA), y: -K * Math.sin(ALPHA), z: 1 };
const moins3 = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const vectoriel = (a, b) => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const scalaire = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const envelopperSolide = (vb, inner, type) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}" data-solide="${type}">${inner}</svg>`;

/**
 * Polyèdre CONVEXE : une face est vue si sa normale sortante regarde vers
 * l'observateur ; une arête est cachée (pointillés) si toutes ses faces le sont.
 */
const polyedre = (V, faces) => {
    const vb = { w: 200, h: 200 };
    const g = V.reduce((s, v) => ({ x: s.x + v.x / V.length, y: s.y + v.y / V.length, z: s.z + v.z / V.length }), { x: 0, y: 0, z: 0 });
    const vue = faces.map((f) => {
        let n = vectoriel(moins3(V[f[1]], V[f[0]]), moins3(V[f[2]], V[f[0]]));
        const c = f.reduce((s, i) => ({ x: s.x + V[i].x / f.length, y: s.y + V[i].y / f.length, z: s.z + V[i].z / f.length }), { x: 0, y: 0, z: 0 });
        if (scalaire(n, moins3(c, g)) < 0) n = { x: -n.x, y: -n.y, z: -n.z };
        return scalaire(n, VUE) < 0;
    });
    const aretes = new Map();
    faces.forEach((f, i) => f.forEach((a, k) => {
        const b = f[(k + 1) % f.length];
        const cle = a < b ? `${a}-${b}` : `${b}-${a}`;
        if (!aretes.has(cle)) aretes.set(cle, { a, b, vue: false });
        if (vue[i]) aretes.get(cle).vue = true;
    }));
    const { map } = ajuster(V.map(proj), vb);
    const P = V.map((v) => map(proj(v)));
    let s = '';
    aretes.forEach((e) => { s += ligne(P[e.a], P[e.b], !e.vue); });
    return { vb, s };
};

/** Pyramide régulière à base de n côtés (n = 3 : le tétraèdre du cours). */
const pyramideN = (n, { r = 1.5, h = 3, rot = 45 } = {}) => {
    const V = [];
    for (let i = 0; i < n; i++) { const t = ((rot + (360 * i) / n) * Math.PI) / 180; V.push({ x: r * Math.cos(t), y: 0, z: r * Math.sin(t) }); }
    V.push({ x: 0, y: h, z: 0 });
    const faces = [[...Array(n).keys()], ...[...Array(n).keys()].map((i) => [i, (i + 1) % n, n])];
    return polyedre(V, faces);
};

/** Prisme droit debout à base régulière de n côtés. */
const prismeN = (n, { r = 1.5, h = 3, rot = -90 } = {}) => {
    const V = [];
    for (let i = 0; i < n; i++) { const t = ((rot + (360 * i) / n) * Math.PI) / 180; V.push({ x: r * Math.cos(t), y: 0, z: r * Math.sin(t) }); }
    for (let i = 0; i < n; i++) V.push({ ...V[i], y: h });
    const bas = [...Array(n).keys()]; const haut = bas.map((i) => i + n);
    const faces = [bas, haut, ...bas.map((i) => [i, (i + 1) % n, ((i + 1) % n) + n, i + n])];
    return polyedre(V, faces);
};

/** Arcs d'une ellipse horizontale : moitié arrière (haut) et moitié avant (bas). */
const arcArriere = (cx, cy, rx, ry, pointilles) => `<path d="M ${cx - rx},${cy} A ${rx},${ry} 0 0 1 ${cx + rx},${cy}" fill="none" stroke="#1f2937" stroke-width="1.7"${pointilles ? ' stroke-dasharray="5 4"' : ''}/>`;
const arcAvant = (cx, cy, rx, ry, pointilles) => `<path d="M ${cx - rx},${cy} A ${rx},${ry} 0 0 0 ${cx + rx},${cy}" fill="none" stroke="#1f2937" stroke-width="1.7"${pointilles ? ' stroke-dasharray="5 4"' : ''}/>`;

/**
 * Un solide du jeu de 185, sans lettre, dans le cadre 200 × 200.
 * type : 'pyramide' (base carrée), 'tetraedre', 'prisme' (p.n = 3, 5 ou 6),
 *        'cone', 'cylindre', 'boule' ; p : proportions (voir chaque cas).
 */
export const solide185 = (type, p = {}) => {
    if (type === 'pyramide') { const { vb, s } = pyramideN(4, p); return envelopperSolide(vb, s, 'pyramide'); }
    if (type === 'tetraedre') { const { vb, s } = pyramideN(3, { rot: -90, ...p }); return envelopperSolide(vb, s, 'tetraedre'); }
    if (type === 'prisme') { const { vb, s } = prismeN(p.n || 3, p); return envelopperSolide(vb, s, `prisme-${p.n || 3}`); }
    const vb = { w: 200, h: 200 };
    if (type === 'cone') return envelopperSolide(vb, coneTraits('correct', p, 'ellipse'), 'cone');
    if (type === 'cylindre') {
        const { rx = 56, ry = 16, h = 110 } = p; const cx = 100; const cyBas = R1(100 + h / 2); const cyHaut = R1(cyBas - h);
        const s = arcArriere(cx, cyBas, rx, ry, true) + arcAvant(cx, cyBas, rx, ry, false)
            + `<ellipse cx="${cx}" cy="${cyHaut}" rx="${rx}" ry="${ry}" fill="none" stroke="#1f2937" stroke-width="1.7"/>`
            + ligne({ x: cx - rx, y: cyHaut }, { x: cx - rx, y: cyBas }) + ligne({ x: cx + rx, y: cyHaut }, { x: cx + rx, y: cyBas });
        return envelopperSolide(vb, s, 'cylindre');
    }
    const { r = 68, ry = 20 } = p;                            // boule
    const s = `<circle cx="100" cy="100" r="${r}" fill="none" stroke="#1f2937" stroke-width="1.7"/>`
        + `<path d="M ${100 - r},100 A ${r},${ry} 0 0 1 ${100 + r},100" fill="none" stroke="#1f2937" stroke-width="1.4" stroke-dasharray="5 4"/>`
        + `<path d="M ${100 - r},100 A ${r},${ry} 0 0 0 ${100 + r},100" fill="none" stroke="#1f2937" stroke-width="1.4"/>`;
    return envelopperSolide(vb, s, 'boule');
};

/**
 * Les traits d'un cône (base horizontale de centre (100 ; cy)), selon la variante.
 * calage 'cercle' : les cinq variantes de coneVariante gardent la même place,
 * calée sur la plus haute (la base en cercle) ; 'ellipse' : le cône seul, centré.
 */
const coneTraits = (variante, { rx = 58, ry = 17, h = 115 } = {}, calage = 'cercle') => {
    const cx = 100; const cy = R1(100 + (h - (calage === 'cercle' ? rx : ry)) / 2);
    const decalage = variante === 'sommet_decale' ? R1(0.6 * rx) : 0;
    const S = { x: cx + decalage, y: R1(cy - h) };
    let base;
    if (variante === 'base_en_cercle') base = arcArriere(cx, cy, rx, rx, true) + arcAvant(cx, cy, rx, rx, false);
    else if (variante === 'cachee_pleine') base = arcArriere(cx, cy, rx, ry, false) + arcAvant(cx, cy, rx, ry, false);
    else if (variante === 'pointilles_inverses') base = arcArriere(cx, cy, rx, ry, false) + arcAvant(cx, cy, rx, ry, true);
    else base = arcArriere(cx, cy, rx, ry, true) + arcAvant(cx, cy, rx, ry, false);
    return base + ligne(S, { x: cx - rx, y: cy }) + ligne(S, { x: cx + rx, y: cy });
};

/**
 * Un cône de révolution dessiné juste ou faux (185, « Quel dessin représente un
 * cône de révolution en perspective cavalière ? ») : mêmes traits, même taille,
 * aucune lettre ; seule la variante change.
 *   correct             : demi-ellipse arrière en pointillés, génératrices aux
 *                         extrémités du grand axe, sommet à l'aplomb du centre ;
 *   cachee_pleine       : toute la base en trait plein (rien de caché) ;
 *   pointilles_inverses : la moitié AVANT en pointillés, l'arrière en plein ;
 *   base_en_cercle      : la base dessinée en cercle (vue de dessus mêlée) ;
 *   sommet_decale       : le sommet n'est plus au-dessus du centre de la base.
 * Le cadre est celui d'un cercle de rayon rx pour les cinq : même taille.
 */
export const coneVariante = (variante, dims = {}) => {
    const vb = { w: 200, h: 200 };
    const inner = coneTraits(variante, dims);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}" data-solide="cone" data-variante="${variante}">${inner}</svg>`;
};
